/**
 * enhancePoochoGurujiCTA — Universal CTA post-processor (Feb 07, 2026)
 *
 * Feature spec:
 *   • हर blog के अंत में एक standard "Poocho Guruji" CTA दिखाना
 *   • Wording, styling, and click tracking are UNIFIED across every category
 *     (rashifal, gochar, vrat, upay, vastu, marriage, career, wealth, health…)
 *   • Only the button is clickable — the surrounding card is decorative
 *   • Click fires GA4 event `cta_poocho_guruji_click` with `source=blog_cta`
 *     and the current blog's slug attached.
 *
 * How it works:
 *   1. Legacy blogs (published pre-Feb-07-2026) still contain the old CTA
 *      wording embedded in their HTML. This enhancer rewrites those to the
 *      new universal copy in-place — no DB migration needed.
 *   2. New blogs already ship the correct copy from the LLM (prompt was
 *      updated in the same session), but this enhancer is idempotent —
 *      calling it on already-correct HTML is a no-op.
 */

const UNIVERSAL_CTA_HTML = `
<div class="cta-box" data-testid="blog-cta" data-blog-cta="1">
  <h3>🕉️ क्या आप अपने जीवन की किसी समस्या से ग्रसित हैं?</h3>
  <p>अपने जन्म विवरण के आधार पर व्यक्तिगत ज्योतिषीय, धार्मिक एवं आध्यात्मिक मार्गदर्शन प्राप्त करें।</p>
  <a href="/poocho-guruji" class="cta-btn" data-testid="blog-cta-btn" data-cta="poocho-guruji">पूछो गुरुजी →</a>
</div>
`.trim();

/**
 * Rewrite any legacy `<div class="cta-box">…</div>` in the provided HTML
 * string to the universal Poocho Guruji CTA. Idempotent.
 *
 * @param {string} html — raw blog HTML
 * @returns {string} — HTML with every CTA box normalized
 */
export function normalizePoochoGurujiCTA(html) {
  if (!html || typeof html !== 'string') return html || '';
  // Match any <div class="cta-box"…>…</div> — greedy body but non-greedy per-div
  // Using a tolerant regex because the LLM sometimes ships extra whitespace
  // or nested inline elements. We do NOT try to parse the HTML with DOMParser
  // here — this runs BEFORE sanitize + inside enhancer pipeline, so a regex
  // pass is the right precision/perf trade-off.
  return html.replace(
    /<div\s+[^>]*class=["'][^"']*\bcta-box\b[^"']*["'][^>]*>[\s\S]*?<\/div>/gi,
    UNIVERSAL_CTA_HTML,
  );
}

/**
 * Attach a delegated click listener to every `.cta-btn` inside the given
 * container element that fires the GA4 event with the current blog slug.
 * Returns a cleanup function.
 *
 * Called via useEffect after the blog HTML mounts. Idempotent — if the
 * container already has our listener attached, the previous one is removed
 * first via the returned cleanup.
 *
 * @param {HTMLElement} container — usually the `.blog-content` div
 * @param {string} blogSlug — current blog's slug for the event payload
 * @param {(source: string, extra?: object) => void} tracker — GA4 tracker fn
 * @returns {() => void} cleanup function
 */
export function attachPoochoGurujiCTATracker(container, blogSlug, tracker) {
  if (!container || typeof tracker !== 'function') return () => {};
  const handler = (evt) => {
    const target = evt.target && evt.target.closest
      ? evt.target.closest('.cta-btn[data-cta="poocho-guruji"], .cta-btn')
      : null;
    if (!target) return;
    // Confirm the click is inside a Poocho Guruji CTA box (not some other .cta-btn)
    const box = target.closest('[data-blog-cta="1"], .cta-box');
    if (!box) return;
    try {
      tracker('blog_cta', { blog_slug: blogSlug || 'unknown' });
    } catch (_) { /* never break the app if analytics fails */ }
    // Let the native <a> navigation proceed — do NOT preventDefault.
  };
  container.addEventListener('click', handler, true);
  return () => container.removeEventListener('click', handler, true);
}

export default normalizePoochoGurujiCTA;
